import React from 'react';
import { AlertTriangle, CloudLightning, Info, Zap } from 'lucide-react';
import { DailySoaringForecast } from '@/lib/mockWeatherData';

interface StormRiskPanelProps {
  forecast: DailySoaringForecast;
}

function riskConfig(risk: number) {
  if (risk < 20) return {
    color: 'var(--success-green)',
    label: 'Faible',
    bg: 'color-mix(in srgb, var(--success-green) 9%, var(--card))',
    border: 'color-mix(in srgb, var(--success-green) 26%, var(--card-border))',
  };
  if (risk < 45) return {
    color: 'var(--warning-orange)',
    label: 'Modere',
    bg: 'color-mix(in srgb, var(--warning-orange) 10%, var(--card))',
    border: 'color-mix(in srgb, var(--warning-orange) 30%, var(--card-border))',
  };
  if (risk < 70) return {
    color: 'var(--danger-red)',
    label: 'Eleve',
    bg: 'color-mix(in srgb, var(--danger-red) 12%, var(--card))',
    border: 'color-mix(in srgb, var(--danger-red) 34%, var(--card-border))',
  };
  return {
    color: 'var(--purple-cloud)',
    label: 'Tres eleve',
    bg: 'color-mix(in srgb, var(--purple-cloud) 13%, var(--card))',
    border: 'color-mix(in srgb, var(--purple-cloud) 34%, var(--card-border))',
  };
}

function liLabel(li: number): string {
  if (li > 2) return 'Stable';
  if (li > 0) return 'Legerement instable';
  if (li > -3) return 'Instable';
  if (li > -6) return 'Tres instable';
  return 'Extremement instable';
}

function liColor(li: number): string {
  if (li > 2) return 'var(--success-green)';
  if (li > 0) return 'var(--warning-orange)';
  if (li > -3) return 'var(--danger-red)';
  return 'var(--purple-cloud)';
}

export default function StormRiskPanel({ forecast }: StormRiskPanelProps) {
  const config = riskConfig(forecast.stormRisk);
  const unstableSignal = forecast.li < -3 || forecast.cape > 600;
  const showStormAlert = forecast.stormRisk >= 45 || unstableSignal;

  return (
    <div
      className="rounded-xl p-4 transition-all duration-200"
      style={{ backgroundColor: config.bg, border: `1px solid ${config.border}` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CloudLightning size={16} style={{ color: config.color }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Risque Orageux</span>
        </div>
        <span className="font-mono-data text-xl font-bold" style={{ color: config.color }}>
          {forecast.stormRisk}%
        </span>
      </div>

      <div className="w-full h-2 rounded-full mb-3" style={{ backgroundColor: 'color-mix(in srgb, var(--text-muted) 20%, transparent)' }}>
        <div
          className="h-2 rounded-full transition-all duration-500"
          style={{ width: `${forecast.stormRisk}%`, backgroundColor: config.color }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>CAPE</p>
          <p className="font-mono-data text-sm font-semibold" style={{ color: forecast.cape > 500 ? 'var(--danger-red)' : 'var(--text-primary)' }}>
            {forecast.cape} J/kg
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>Lifted Index</p>
          <p className="font-mono-data text-sm font-semibold" style={{ color: liColor(forecast.li) }}>
            {forecast.li > 0 ? '+' : ''}{forecast.li}
          </p>
          <p className="text-xs mt-0.5" style={{ color: liColor(forecast.li) }}>{liLabel(forecast.li)}</p>
        </div>
      </div>

      {showStormAlert && (
        <div
          className="flex items-start gap-2 mt-3 p-2.5 rounded-lg"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--danger-red) 12%, var(--card))',
            border: '1px solid color-mix(in srgb, var(--danger-red) 34%, var(--card-border))',
          }}
        >
          <Zap size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--danger-red)' }} />
          <div className="space-y-0.5">
            <p className="text-xs font-semibold" style={{ color: 'var(--danger-red)' }}>
              Risque d'orage pendant la fenetre de vol
            </p>
            {forecast.li < -3 && (
              <p className="text-xs" style={{ color: 'var(--danger-red)' }}>
                Lifted Index bas (LI = {forecast.li}) - atmosphere instable
              </p>
            )}
            {forecast.cape > 600 && (
              <p className="text-xs" style={{ color: 'var(--danger-red)' }}>
                CAPE elevee ({forecast.cape} J/kg) - convection profonde possible
              </p>
            )}
          </div>
        </div>
      )}

      {forecast.stormRisk >= 45 && !unstableSignal && (
        <div
          className="flex items-start gap-2 mt-3 p-2.5 rounded-lg"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--danger-red) 10%, var(--card))',
            border: '1px solid color-mix(in srgb, var(--danger-red) 30%, var(--card-border))',
          }}
        >
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--danger-red)' }} />
          <p className="text-xs" style={{ color: 'var(--danger-red)' }}>
            Signal orageux a confirmer avec la meteo officielle avant de voler.
          </p>
        </div>
      )}

      {forecast.stormRisk < 20 && !showStormAlert && (
        <div
          className="flex items-start gap-2 mt-3 p-2.5 rounded-lg"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--success-green) 9%, var(--card))',
            border: '1px solid color-mix(in srgb, var(--success-green) 24%, var(--card-border))',
          }}
        >
          <Info size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--success-green)' }} />
          <p className="text-xs" style={{ color: 'var(--success-green)' }}>
            Pas de signal orageux marque dans les parametres disponibles.
          </p>
        </div>
      )}
    </div>
  );
}
